import { FC, useEffect, useMemo } from 'react';
import { List } from 'antd';
import { useQuery } from '@apollo/client';
import { StyledListItemContent } from 'theme/styled';
import {
  GET_REPORTS_BY_PATIENT_ID,
  GET_REPORTS_FILE_BY_PATIENT_ID
} from 'gql/patients/queries';
import { GetReportsByPatientId } from 'gql/patients/__generated__/GetReportsByPatientId';
import { GetReportsFileByPatientId } from 'gql/patients/__generated__/GetReportsFileByPatientId';
import dayjs from 'dayjs';

import { showErrorMessage } from 'utils/showErrorMessage';
import { ReportCard, ReportFileCard } from 'components/shared';

interface IProps {
  id: string;
  setTotal(total: number): void;
}

const PAGE_LIMIT = 9999;

const Reports: FC<IProps> = ({ id, setTotal }) => {
  const { data: reports, loading: reportsLoading } =
    useQuery<GetReportsByPatientId>(GET_REPORTS_BY_PATIENT_ID, {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables: {
        id,
        query: {
          page: 0,
          limit: PAGE_LIMIT
        }
      },
      onError: err => showErrorMessage(err)
    });

  const { data: reportsFiles, loading: reportsFilesLoading } =
    useQuery<GetReportsFileByPatientId>(GET_REPORTS_FILE_BY_PATIENT_ID, {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      variables: {
        id,
        query: {
          page: 0,
          limit: PAGE_LIMIT
        }
      },
      onError: err => showErrorMessage(err)
    });

  const loading = reportsLoading || reportsFilesLoading;

  const results = useMemo(() => {
    const data = [
      ...(reports?.getReportsByPatientId?.data.results || []),
      ...(reportsFiles?.getReportsFileByPatientId?.data.results || [])
    ].map(item => ({
      ...item,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      sortDate: item.created_at || item.date_report
    }));

    data.sort((a, b) =>
      dayjs(a.sortDate).isAfter(dayjs(b.sortDate)) ? -1 : 1
    );

    return data;
  }, [
    reports?.getReportsByPatientId?.data.results,
    reportsFiles?.getReportsFileByPatientId?.data.results
  ]);

  useEffect(() => {
    setTotal(results.length);
  }, [results.length, setTotal]);

  return (
    <StyledListItemContent>
      <List
        loading={loading}
        dataSource={results}
        renderItem={item => (
          <List.Item className="list-content-item">
            {item.__typename === 'Report' ? (
              <ReportCard
                id={item.id}
                appointmentId={item.appointment_id}
                dateReport={item.date_report}
                report={item.report}
                status={item.status}
              />
            ) : (
              <ReportFileCard
                id={item.id}
                file={item.file}
                note={item.note}
                date={item.created_at}
              />
            )}
          </List.Item>
        )}
      />
    </StyledListItemContent>
  );
};

export default Reports;
